import React, { useState } from 'react';
// @ts-ignore
import styles from './QuestionnairePlanning.module.scss';
import QuestionnairePlanningDisposableForm from './QuestionnairePlanningDisposableForm';
import QuestionnairePlanningPeriodicalForm from './QuestionnairePlanningPeriodicalForm';
import { joinClasses } from '../../../../Utils/className.util';
import FirstUpper from '../../../../Components/FirstUpper';
import useTranslation from '../../../../Hooks/useTranslation';
import moment, { Moment } from 'moment/moment';
import { useAppContext } from '../../../../Services/AppContext';
import apiClient from '../../../../Services/ApiClient';
import useNotification from '../../../../Hooks/useNotification.hook';
import { QuestionnaireSchedule } from '../../../../Components/QuestionnaireList/QuestionnaireListItem';
import { useLazyQuestionnaireScheduleQuery } from '../../../../graphql/graphql';
import { useEffectAsync } from '../../../../helpers';
import OverlayLoading from '../../../../Components/OverlayLoading';
import useWorkplaceRole from '../../../Workplace/Hooks/useWorkplaceRole';
import { getMomentDate } from '../../../../Utils/dates.util';
import { QuestionnaireModule } from '../../Enums/QuestionnaireModule.enum';

interface QuestionnairePlanningProps {
	onClose: any;
	patientId?: string;
	schedule?: QuestionnaireSchedule;
	scheduleId?: string;
	questionnaireId?: string;
	module?: QuestionnaireModule;
}

export default function QuestionnairePlanning(
	props: QuestionnairePlanningProps,
) {
	const { onClose, schedule, patientId, scheduleId, questionnaireId, module } =
		props;
	const [activeTab, setActiveTab] = useState('disposable');
	const { t } = useTranslation();
	const { userData } = useAppContext();
	const { showNotificationSuccess, showNotificationAlert } = useNotification();
	const [getQuestionnaireSchedule] = useLazyQuestionnaireScheduleQuery();
	const [initialized, setInitialized] = useState<boolean>(false);
	const [defaultData, setDefaultData] = useState<any>({});
	const [isLoading, setIsLoading] = useState(false);
	const workplaceId = userData.workplaceId;
	const userId = userData.id;
	const { isNurse, isDone } = useWorkplaceRole({ userId, workplaceId });

	const handleIsNow = (date: Moment, time?: Moment) => {
		const isToday = moment().isSame(date, 'day');
		return isToday ? !time : false;
	};

	const handleSave = async (values: any) => {
		setIsLoading(true);
		let showTimeWarning = false;
		const newSchedule = {
			questionnaireId: values.questionnaire.value,
			doctorId: values.doctor.value,
			patientId: values.patient.value,
			dateFrom: values.dateFrom.format('yyyy-MM-DD'),
			dateTo: values.dateTo ? values.dateTo.format('yyyy-MM-DD') : undefined,
			time: values.time ? values.time.format('HH:mm') : null,
			isNow: handleIsNow(values.dateFrom, values.time),
			workplaceId: values.workplace.value,
			repeatHours: undefined,
		};

		if (values.time) {
			newSchedule.dateFrom = moment(
				values.dateFrom.format('yyyy-MM-DD') +
					' ' +
					values.time.format('HH:mm'),
			);
		} else {
			if (!newSchedule.isNow) {
				newSchedule.dateFrom = getMomentDate(values.dateFrom).set({
					hour: 8,
					minute: 0,
					second: 0,
				});
				showTimeWarning = true;
			}
		}

		if (values.repeatHours) {
			newSchedule.repeatHours = values.repeatHours.value;
		}

		if (values?.frequencyType === '0') {
			const apiItems = [];
			for (const itemId of Object.keys(values.items)) {
				apiItems.push({
					questionnaireItemId: itemId,
					repeatHours: values.items[itemId]?.repeatHours?.value,
				});
			}
			newSchedule.items = apiItems;
		}

		if (moment().isAfter(newSchedule.dateFrom) && !newSchedule.isNow) {
			showNotificationAlert({
				content: t('component.questionnaires.timeInPast'),
			});
			setIsLoading(false);
			return;
		}

		let result;
		if (schedule) {
			result = await apiClient.patch(
				'/questionnaire/schedule/' + schedule.id,
				newSchedule,
			);
		} else {
			result = await apiClient.post(
				'/user/' +
					newSchedule.patientId +
					'/questionnaire/' +
					newSchedule.questionnaireId +
					'/schedule',
				newSchedule,
			);
		}

		if (!result?.data?.error) {
			showNotificationSuccess({
				content: t('component.questionnaires.questionnairePlanned'),
			});
			if (showTimeWarning) {
				showNotificationSuccess({
					content: t('component.questionnaires.timeWarning'),
				});
			}
		}

		onClose(true);
		setIsLoading(false);
	};

	const handleChangeTab = (tab: string) => {
		setActiveTab(tab);
	};

	useEffectAsync(async () => {
		setInitialized(false);

		let usedSchedule = schedule;
		if (scheduleId && !schedule) {
			const { questionnaireSchedule } = await getQuestionnaireSchedule({
				id: scheduleId,
			}).unwrap();

			usedSchedule = questionnaireSchedule;
		}

		if (usedSchedule?.type) {
			setActiveTab(usedSchedule.type);
		}

		setDefaultData({
			id: usedSchedule?.id,
			doctor: {
				value: usedSchedule?.createdBy?.id || isNurse ? undefined : userData.id,
			},
			patient: {
				value: usedSchedule?.user?.id || patientId,
			},
			workplace: workplaceId,
			questionnaire: {
				value: usedSchedule?.questionnaireId ?? questionnaireId ?? undefined,
			},
			dateFrom: moment(usedSchedule?.dateFrom),
			dateTo: usedSchedule?.dateTo ? moment(usedSchedule?.dateTo) : undefined,
			time: usedSchedule ? moment(usedSchedule?.dateFrom) : undefined,
			repeatHours: usedSchedule?.repeatHours,
		});

		if (isDone) {
			setInitialized(true);
		}
	}, [isNurse, isDone]);

	if (!initialized) {
		return <></>;
	}

	return (
		<>
			{isLoading && <OverlayLoading />}
			<div className={styles.tabControlWrapper}>
				<div className={styles.primaryControls}>
					<div
						className={joinClasses([
							styles.tabButton,
							activeTab === 'disposable' && styles.tabButtonActive,
						])}
						onClick={() => handleChangeTab('disposable')}>
						<FirstUpper>{t('component.time.oneTime')}</FirstUpper>
					</div>
					<div
						className={joinClasses([
							styles.tabButton,
							activeTab === 'periodical' && styles.tabButtonActive,
						])}
						onClick={() => handleChangeTab('periodical')}>
						<FirstUpper>{t('component.time.regular')}</FirstUpper>
					</div>
				</div>
			</div>
			{activeTab === 'disposable' && (
				<div className={styles.tab}>
					<QuestionnairePlanningDisposableForm
						addDoctor={true}
						defaultData={defaultData}
						onSubmit={handleSave}
						module={module}
					/>
				</div>
			)}
			{activeTab === 'periodical' && (
				<div className={styles.tab}>
					<QuestionnairePlanningPeriodicalForm
						addDoctor={true}
						defaultData={defaultData}
						onSubmit={handleSave}
						module={module}
					/>
				</div>
			)}
		</>
	);
}
